/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import {
  WifiOff,
  AlertTriangle,
  RotateCcw,
  Sparkles,
  Layers,
  LayoutGrid,
} from 'lucide-react';

export const SqueletteChargement: React.FC = () => {
  return (
    <div
      className="flex-1 w-full h-full flex flex-col items-center justify-center p-6 bg-slate-50 dark:bg-slate-900 animate-pulse select-none"
      role="status"
      aria-label="Chargement du plan de salle en cours..."
    >
      <div className="w-16 h-16 rounded-2xl bg-slate-200 dark:bg-slate-800 mb-4" />
      <div className="h-5 w-48 bg-slate-200 dark:bg-slate-800 rounded mb-2" />
      <div className="h-3 w-64 bg-slate-200 dark:bg-slate-800 rounded" />

      {/* Tables virtuelles squelette */}
      <div className="grid grid-cols-3 gap-6 mt-10 opacity-30">
        <div className="w-20 h-20 rounded-full bg-slate-300 dark:bg-slate-700" />
        <div className="w-28 h-20 rounded-lg bg-slate-300 dark:bg-slate-700" />
        <div className="w-20 h-20 rounded-lg bg-slate-300 dark:bg-slate-700" />
      </div>
    </div>
  );
};

export const EcranErreur: React.FC<{
  message?: string;
  onReessayer: () => void;
}> = ({ message = 'Une erreur est survenue lors du chargement du plan.', onReessayer }) => {
  return (
    <div
      className="flex-1 w-full h-full flex flex-col items-center justify-center p-6 bg-red-50/50 dark:bg-red-950/20 text-center select-none"
      role="alert"
    >
      <div className="p-3 rounded-2xl bg-red-100 text-red-600 dark:bg-red-900/60 dark:text-red-300 mb-3">
        <AlertTriangle className="w-8 h-8" />
      </div>
      <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-1">
        Erreur de chargement du plan
      </h2>
      <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mb-4">
        {message}
      </p>
      <button
        type="button"
        onClick={onReessayer}
        aria-label="Réessayer de charger le plan"
        className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 cursor-pointer min-h-[44px]"
      >
        <RotateCcw className="w-4 h-4" />
        <span>Réessayer</span>
      </button>
    </div>
  );
};

export const EcranPlanVide: React.FC<{
  onDemarrerDepuisFormats: () => void;
  onDemarrerDepuisModele: () => void;
}> = ({ onDemarrerDepuisFormats, onDemarrerDepuisModele }) => {
  return (
    <div
      className="flex-1 w-full h-full flex flex-col items-center justify-center p-6 bg-slate-50/70 dark:bg-slate-900/60 text-center select-none"
      role="region"
      aria-label="Démarrage guidé d'un plan vide"
    >
      <div className="p-4 rounded-3xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm mb-4">
        <LayoutGrid className="w-10 h-10 text-slate-700 dark:text-slate-200" />
      </div>
      <h2 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-1">
        Votre plan de salle est encore vide
      </h2>
      <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mb-6 leading-relaxed">
        Démarrez facilement en déposant vos tables depuis votre catalogue de formats ou chargez un modèle prêt à l'emploi.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={onDemarrerDepuisFormats}
          className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer min-h-[44px]"
        >
          <Layers className="w-4 h-4 text-emerald-400" />
          <span>Créer ma salle à partir de formats</span>
        </button>
        <button
          type="button"
          onClick={onDemarrerDepuisModele}
          className="px-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 text-slate-800 dark:text-slate-200 font-bold text-xs flex items-center justify-center gap-2 cursor-pointer min-h-[44px]"
        >
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span>Partir d’un modèle type brasserie</span>
        </button>
      </div>
    </div>
  );
};

export const BanniereHorsLigne: React.FC = () => {
  return (
    <div
      className="bg-slate-800 text-amber-300 px-3 py-1.5 flex items-center justify-between text-xs font-semibold select-none border-b border-slate-700"
      role="status"
    >
      <div className="flex items-center gap-2">
        <WifiOff className="w-4 h-4 text-amber-400" />
        <span>Mode hors ligne actif — vos modifications de brouillon restent intactes sur cet écran</span>
      </div>
      <span className="text-[10px] bg-slate-700 px-2 py-0.5 rounded text-slate-300 hidden sm:inline">
        Synchronisation automatique à la reconnexion
      </span>
    </div>
  );
};
